/**
 * Trade Quotes Page — lists quotes with search/sort, renders
 * single-quote detail views, and handles inline quantity/item edits.
 *
 * Loaded by sections/trade-quotes.liquid.
 * Depends on QuoteService (window.QuoteService) for all API calls.
 * Uses TradePageController for list/detail view toggling and
 * TradeSearchSortController for client-side filtering.
 */
(function () {
  document.addEventListener('DOMContentLoaded', () => {
    const page = document.getElementById('trade-quotes-page');
    if (!page) return;

    const isApproved = page.dataset.approved === 'true';
    const isPreApproved = page.dataset.preApproved === 'true';
    const customerEmail = page.dataset.customerEmail;

    window.isApprovedTradeCustomer = isApproved;
    window.isPreApprovedTradeCustomer = isPreApproved;

    const quoteListContainer = document.querySelector('.trade-list');
    const headerControls = document.querySelector('.trade-header-controls');

    const quotesListView = document.getElementById('quotes-list-view');
    const singleQuoteView = document.getElementById('single-quote-view');
    const loadingState = document.getElementById('loading-state');
    const loadingMessage = document.getElementById('loading-message');

    // Cache search and sort elements
    const searchInput = document.getElementById('quotes-search');
    const sortSelect = document.getElementById('quotes-sort');
    const searchContainer = searchInput ? searchInput.closest('.search-container') : null;
    const sortContainer = sortSelect ? sortSelect.closest('.sort-container') : null;

    // Store original quotes data for filtering/sorting
    let originalQuotes = [];
    let pendingActiveQuoteCartEdit = false;
    const quoteListConfig = {
      features: {
        showHeaders: false,
        clickableRows: true,
        showSearch: true,
        showSort: true,
        showStatusBadge: true,
        showAssignedUsers: true,
        showOpenLink: true,
        showMenuIndicator: true
      }
    };

    const renderTradeState = (options = {}) => window.renderTradeState({
      ...options,
      containerEl: quoteListContainer
    });
    const pageController = window.createTradePageController({
      idParam: 'id',
      listViewEl: quotesListView,
      detailViewEl: singleQuoteView,
      loadingStateEl: loadingState,
      loadingMessageEl: loadingMessage,
      defaultLoadingMessage: 'Loading quotes...'
    });
    const getQuoteIdFromURL = pageController.getIdFromURL;
    const toggleView = pageController.toggleView;
    const showLoading = pageController.showLoading;

    // Search and filter functions
    const filterQuotes = (quotes, searchTerm) => {
      if (!searchTerm.trim()) return quotes;

      const term = searchTerm.toLowerCase();
      return quotes.filter(quote => {
        return (
          quote.displayId.toString().toLowerCase().includes(term) ||
          quote.title.toLowerCase().includes(term) ||
          quote.shippingAddress.city.toLowerCase().includes(term) ||
          quote.shippingAddress.address1.toLowerCase().includes(term) ||
          (quote.shippingAddress.address2 && quote.shippingAddress.address2.toLowerCase().includes(term))
        );
      });
    };

    const sortQuotes = (quotes, sortOrder) => window.sortByCreatedAt(quotes, sortOrder, (quote) => quote.createdAt);
    const searchSortController = window.createTradeSearchSortController({
      searchInput,
      sortSelect,
      filterFn: filterQuotes,
      sortFn: sortQuotes,
      renderFn: renderQuotes,
      debounceMs: 1000,
      initialItems: []
    });

    function showCheckoutError(message) {
      const alert = document.querySelector('[data-tpl-checkout-error]');
      const text = document.querySelector('[data-tpl-checkout-error-text]');
      if (!alert || !text) return;
      text.textContent = message;
      alert.style.display = '';
    }

    function hideCheckoutError() {
      const alert = document.querySelector('[data-tpl-checkout-error]');
      if (alert) alert.style.display = 'none';
    }

    function isViewingActiveQuote(quoteId) {
      if (!window.QuoteService || !quoteId) return false;
      return String(window.QuoteService.getActiveQuoteId()) === String(quoteId);
    }

    function finishPendingActiveQuoteCartEdit() {
      if (!pendingActiveQuoteCartEdit) return;

      // Background quote refreshes should re-render in place. Only
      // unwind the loading view after a user-initiated cart edit.
      pendingActiveQuoteCartEdit = false;
      toggleView();
    }

    function updateInactiveQuoteItems(quoteId, items) {
      return QuoteService.updateQuote(quoteId, { items })
        .then(() => fetchQuote(quoteId))
        .catch((error) => {
          if (window.trackTradeError) {
            trackTradeError(error, { source: 'TradeQuotes.updateInactiveQuoteItems', quoteId });
          }
          toggleView();
        });
    }

    function renderQuotes(quotes) {
      quoteListContainer.innerHTML = '';
      if (quotes && quotes.length > 0) {
        quoteListContainer.innerHTML = window.renderTradeDataList(
          window.createTradeRecordListConfig('quote', {
            rows: quotes,
            features: quoteListConfig.features
          })
        );
      } else if (searchInput && searchInput.value.trim()) {
        quoteListContainer.innerHTML = renderTradeState({
            type: 'empty',
            title: 'No matching quotes',
            message: 'We couldn\'t find any quotes matching your search.',
          });
      } else {
        quoteListContainer.innerHTML = renderTradeState({
            type: 'empty',
            title: 'You have no quotes yet',
            message: 'Once you have created a quote, it will appear here.',
            actionText: 'Start Shopping',
            actionUrl: '/pages/outdoor-collections',
          });
      }
    }

    const renderSingleQuote = (quote) => {
      if (!quote) return;

      const hasNetsuiteOnlyItems = quote.items && quote.items.some(item => item.netsuiteExclusiveSku === true);
      const isExpiredQuote = quote.status && quote.status.toLowerCase() === 'expired';
      const formatCurrency = window.formatTradeAmount;

      window.buildTradeRecordDetail(quote, {
        containerEl: singleQuoteView,
        type: 'quote',
        dateField: 'expiresAt',
        headerTemplateId: 'tpl-quote-detail-header',
        itemTemplateId: 'tpl-quote-detail-item',
        optionTemplateId: 'tpl-quote-item-option',
        footerTemplateId: 'tpl-quote-detail-footer',
        showDiscount: window.isApprovedTradeCustomer,

        buildEmptyState: function () {
          const empty = document.createElement('div');
          empty.className = 'trade-empty-state';
          const h1 = document.createElement('h1');
          h1.className = 'text-xl';
          h1.textContent = 'No items in this quote.';
          const p = document.createElement('p');
          p.className = 'text-md';
          p.textContent = 'Add an item to your cart to get started.';
          const a = document.createElement('a');
          a.href = '/pages/outdoor-collections';
          a.className = 'Button Button--primary Button--mobile-full Button--small';
          a.textContent = 'START SHOPPING';
          empty.appendChild(h1);
          empty.appendChild(p);
          empty.appendChild(a);
          return empty;
        },

        populateItem: function (clone, item) {
          const isNetsuiteOnly = item.netsuiteExclusiveSku === true;
          const isReadOnly = isNetsuiteOnly || isExpiredQuote;
          const root = clone.querySelector('.quote-item');
          root.setAttribute('data-variant-id', item.id);

          if (isNetsuiteOnly) {
            root.setAttribute('data-netsuite-only', 'true');
          }

          if (isReadOnly) {
            clone.querySelector('[data-tpl-qty-selector]').remove();
            clone.querySelector('[data-tpl-desktop-qty-selector]').remove();
            clone.querySelector('[data-tpl-remove-btn]').remove();
            const mobileStatic = clone.querySelector('[data-tpl-qty-static]');
            mobileStatic.textContent = 'Qty: ' + item.quantity;
            mobileStatic.style.display = '';
            const desktopStatic = clone.querySelector('[data-tpl-desktop-qty-static]');
            desktopStatic.textContent = 'Qty: ' + item.quantity;
            desktopStatic.style.display = '';
          } else {
            clone.querySelector('[data-tpl-qty-static]').remove();
            clone.querySelector('[data-tpl-desktop-qty-static]').remove();
            clone.querySelector('[data-tpl-mobile-qty-value]').textContent = item.quantity;
            clone.querySelector('[data-tpl-desktop-qty-value]').textContent = item.quantity;
          }

          // Stock info
          const formattedArrival = item.estimatedArrival || null;
          const stockQty = item.inventoryQuantity || 0;
          const itemQty = item.quantity;
          if (stockQty > 0) {
            if (itemQty > stockQty) {
              const overQty = itemQty - stockQty;
              if (formattedArrival) {
                const partialArrival = clone.querySelector('[data-tpl-stock-partial-arrival]');
                partialArrival.textContent = stockQty + ' In Stock and ' + overQty + ' with an Estimated Arrival of ' + formattedArrival;
                partialArrival.style.display = '';
              } else {
                const partialBackorder = clone.querySelector('[data-tpl-stock-partial-backorder]');
                partialBackorder.querySelector('[data-tpl-stock-partial-text]').textContent = stockQty + ' In Stock and ' + overQty;
                partialBackorder.style.display = '';
              }
            } else {
              clone.querySelector('[data-tpl-stock-in-stock]').style.display = '';
            }
          } else {
            if (formattedArrival) {
              const arrivalEl = clone.querySelector('[data-tpl-stock-arrival]');
              arrivalEl.textContent = 'Estimated Arrival: ' + formattedArrival;
              arrivalEl.style.display = '';
            } else {
              clone.querySelector('[data-tpl-stock-fallback]').style.display = '';
            }
          }

          // Product link
          if (!isNetsuiteOnly) {
            const safeUrl = window.sanitizeTradeUrl(item.url);
            if (safeUrl) {
              const link = clone.querySelector('[data-tpl-product-link]');
              link.href = safeUrl;
              link.style.display = '';
              clone.querySelector('[data-tpl-product-link-placeholder]').remove();
            } else {
              clone.querySelector('[data-tpl-product-link]').remove();
            }
          } else {
            clone.querySelector('[data-tpl-product-link]').remove();
          }
        },

        populateFooter: function (clone, ctx) {
          // Export button
          if (window.isApprovedTradeCustomer) {
            const exportBtn = clone.querySelector('[data-tpl-export-btn]');
            exportBtn.setAttribute('data-export-id', quote.id);
            exportBtn.setAttribute('data-export-display-id', quote.displayId);
            exportBtn.setAttribute('data-export-title', quote.title);
            exportBtn.setAttribute('data-export-date', ctx.formattedDate);
            exportBtn.setAttribute('data-export-total', formatCurrency(quote.summary.total));
            exportBtn.style.display = '';
          }

          // Pay button
          if (!isExpiredQuote && (window.isPreApprovedTradeCustomer || (window.isApprovedTradeCustomer && !hasNetsuiteOnlyItems))) {
            clone.querySelector('[data-tpl-pay-btn]').style.display = '';
          }

          // Extend button — expired quotes only; approval-gated like export since it hits an authenticated endpoint.
          // NetSuite-only quotes can't be repriced against Shopify, so they get the expired notice instead.
          if (isExpiredQuote && window.isApprovedTradeCustomer && !hasNetsuiteOnlyItems) {
            clone.querySelector('[data-tpl-extend-btn]').style.display = '';
          }

          // NetSuite-only alert — suppressed when expired, since the expired notice covers it
          if (hasNetsuiteOnlyItems && !isExpiredQuote) {
            clone.querySelector('[data-tpl-netsuite-alert]').style.display = '';
          }
        }
      });

      if (isExpiredQuote) {
        const editBtn = singleQuoteView.querySelector('#open-edit-modal');
        if (editBtn) editBtn.remove();

        const dateLabel = singleQuoteView.querySelector('[data-tpl-date-label]');
        if (dateLabel) dateLabel.textContent = 'Expired on';

        // Only NetSuite-only quotes get the notice — portal quotes get the Extend button instead
        if (hasNetsuiteOnlyItems) {
          const buildExpiredNotice = () => {
            const notice = window.cloneTemplate('tpl-quote-expired-notice');
            const rep = quote.salesRep;
            if (rep && rep.name && rep.email) {
              notice.querySelector('[data-tpl-expired-rep-name]').textContent = rep.name;
              const emailLink = notice.querySelector('[data-tpl-expired-rep-email]');
              emailLink.textContent = rep.email;
              emailLink.href = 'mailto:' + rep.email;
              notice.querySelector('[data-tpl-expired-rep]').style.display = '';
              notice.querySelector('[data-tpl-expired-generic]').style.display = 'none';
            }
            return notice;
          };
          const desktopSummary = singleQuoteView.querySelector('.quote-summary-desktop');
          if (desktopSummary) {
            desktopSummary.after(buildExpiredNotice());
          }
          const mobileSummary = singleQuoteView.querySelector('.trade-page-footer .quote-summary');
          if (mobileSummary) {
            mobileSummary.after(buildExpiredNotice());
          }
        }
      }
    };

    const quotesListFooter = quotesListView ? quotesListView.querySelector('.trade-page-footer') : null;

    const hideQuotesListFooter = () => { if (quotesListFooter) quotesListFooter.style.display = 'none'; };
    const showQuotesListFooter = () => { if (quotesListFooter) quotesListFooter.style.display = ''; };

    const fetchQuotes = async () => {
      if (!window.QuoteService) {
        console.error('QuoteService unavailable');
        quoteListContainer.innerHTML = renderTradeState({
          type: 'error',
          title: 'We were unable to load your quotes',
          actionText: 'Try Again',
          onRetry: fetchQuotes,
        });
        hideQuotesListFooter();
        toggleView();
        return;
      }

      await window.runTradeFetchFlow({
        before: () => showLoading(),
        request: async () => QuoteService.fetchQuotes(),
        onSuccess: (quotesArray) => {
          originalQuotes = Array.isArray(quotesArray) ? quotesArray : [];
          searchSortController.setItems(originalQuotes);
          searchSortController.apply();
          showQuotesListFooter();
        },
        onError: (error) => {
          console.error('Error fetching quotes:', error);
          quoteListContainer.innerHTML = renderTradeState({
            type: 'error',
            title: 'We were unable to load your quotes',
            actionText: 'Try Again',
            onRetry: fetchQuotes,
          });
          hideQuotesListFooter();
        },
        after: () => toggleView()
      });
    };

    const fetchQuote = async (id) => {
      if (!window.QuoteService) {
        console.error('QuoteService unavailable');
        return;
      }

      await window.runTradeFetchFlow({
        before: () => showLoading(),
        request: async () => {
          const quote = await QuoteService.fetchQuote(id);
          if (!quote) throw new Error('No quote data received');
          return quote;
        },
        onSuccess: (quote) => {
          if (quote.status && quote.status.toLowerCase() === 'processing') {
            window.location.href = '/pages/quotes';
            return;
          }
          renderSingleQuote(quote);
          if (window.TradeEditQuoteModal && typeof window.TradeEditQuoteModal.render === 'function') {
            window.TradeEditQuoteModal.render(quote);
          }
        },
        onError: (err) => {
          if (window.trackTradeError) trackTradeError(err, { source: 'TradeQuotes.fetchQuote', quoteId: id });
          window.location.href = '/pages/quotes';
        },
        after: () => toggleView()
      });
    };

    window.bindTradeSummaryToggle();
    window.bindTradeSummaryResizeReset();
    window.syncTradeListControls({
      features: quoteListConfig.features,
      headerControlsEl: headerControls,
      searchContainerEl: searchContainer,
      sortContainerEl: sortContainer
    });
    window.bindTradeItemNavigation({
      onMobileListClick: (quoteItem) => {
        if (!quoteItem) return;
        if (quoteItem.closest('.trade-list-item--processing')) return;
        const linkedOrderId = quoteItem.getAttribute('data-linked-order-id');
        if (linkedOrderId) {
          window.location.href = `/pages/orders?id=${linkedOrderId}`;
          return;
        }
        const id = quoteItem.getAttribute('data-quote-id');
        if (id) window.location.href = `/pages/quotes?id=${id}`;
      },
      shouldSkipMobileProductClick: (quoteItemProduct) => quoteItemProduct && quoteItemProduct.getAttribute('data-netsuite-only') === 'true',
      shouldSkipDesktopImageClick: (parentItem) => parentItem && parentItem.getAttribute('data-netsuite-only') === 'true'
    });
    if (window.TradeEditQuoteModal && typeof window.TradeEditQuoteModal.bind === 'function') {
      window.TradeEditQuoteModal.bind({
        getCurrentQuoteId: getQuoteIdFromURL,
        onSave: ({ quoteId, updateData }) => {
          return QuoteService.updateQuote(quoteId, updateData).then(() => fetchQuote(quoteId));
        }
      });
    }

    // Use event delegation on document for dynamically created elements
    document.addEventListener('click', (e) => {
      const target = e.target;
      // Handle quantity adjust within a quote item
      const qtyBtn = target && target.closest && target.closest('.qty-btn-trade');
      if (qtyBtn && target.closest('.quote-item')) {
        e.preventDefault();
        const quoteItemEl = target.closest('.quote-item');
        const currentQuoteId = getQuoteIdFromURL();
        if (!quoteItemEl || !currentQuoteId) return;

        // Prevent modification of NetSuite-only items
        if (quoteItemEl.getAttribute('data-netsuite-only') === 'true') return;

        const qtyValueEl = quoteItemEl.querySelector('.qty-value');
        const currentQty = qtyValueEl ? parseInt(qtyValueEl.textContent, 10) || 1 : 1;
        const action = qtyBtn.getAttribute('data-action');
        const itemVariantId = quoteItemEl.getAttribute('data-variant-id');
        const itemSku = quoteItemEl.getAttribute('data-sku');

        let newQty = currentQty;
        if (action === 'increase') newQty = currentQty + 1;
        if (action === 'decrease') newQty = Math.max(1, currentQty - 1);

        // Show loading screen immediately
        showLoading('Updating quote...');

        if (isViewingActiveQuote(currentQuoteId)) {
          pendingActiveQuoteCartEdit = true;

          QuoteService.updateActiveQuoteCartItem(itemVariantId, itemSku, currentQty, newQty)
            .catch(() => {
              finishPendingActiveQuoteCartEdit();
            });
          return;
        }

        // Build items payload from current DOM to send full list expected by backend
        const itemEls = Array.from(document.querySelectorAll('.quote-items .quote-item'));
        const items = itemEls.map((el) => {
          const elSku = el.getAttribute('data-sku');
          const elVariantId = el.getAttribute('data-variant-id');
          const elQty = parseInt(el.querySelector('.qty-value')?.textContent || '1', 10) || 1;
          return {
            sku: elSku,
            variantId: elVariantId,
            quantity: el === quoteItemEl ? newQty : elQty,
          };
        });

        updateInactiveQuoteItems(currentQuoteId, items)
        return;
      }

      // Handle remove item
      if (target && target.classList && target.classList.contains('remove-item-btn')) {
        e.preventDefault();
        const quoteItemEl = target.closest('.quote-item');
        const currentQuoteId = getQuoteIdFromURL();
        if (!quoteItemEl || !currentQuoteId) return;

        // Prevent removal of NetSuite-only items
        if (quoteItemEl.getAttribute('data-netsuite-only') === 'true') return;

        const itemToRemoveVariantId = quoteItemEl.getAttribute('data-variant-id');
        const itemToRemoveSku = quoteItemEl.getAttribute('data-sku');
        const currentQty = parseInt(quoteItemEl.querySelector('.qty-value')?.textContent || '1', 10) || 1;

        // Show loading screen immediately
        showLoading('Updating quote...');

        if (isViewingActiveQuote(currentQuoteId)) {
          pendingActiveQuoteCartEdit = true;

          QuoteService.updateActiveQuoteCartItem(itemToRemoveVariantId, itemToRemoveSku, currentQty, 0)
            .catch(() => {
              finishPendingActiveQuoteCartEdit();
            });
          return;
        }

        // Build items payload excluding the removed item
        const itemEls = Array.from(document.querySelectorAll('.quote-items .quote-item'));
        const items = itemEls
          .filter((el) => {
            const variantId = el.getAttribute('data-variant-id');
            if (itemToRemoveVariantId) return variantId !== itemToRemoveVariantId;
            return el.getAttribute('data-sku') !== itemToRemoveSku;
          })
          .map((el) => ({
            sku: el.getAttribute('data-sku'),
            variantId: el.getAttribute('data-variant-id'),
            quantity: parseInt(el.querySelector('.qty-value')?.textContent || '1', 10) || 1,
          }));

        updateInactiveQuoteItems(currentQuoteId, items);
        return;
      }

      // Handle pay quote button - checkout functionality
      if (target && target.id === 'pay-quote-btn') {
        e.preventDefault();

        const currentQuoteId = getQuoteIdFromURL();
        if (!currentQuoteId) {
          console.error('No quote ID found.');
          return;
        }

        hideCheckoutError();

        // Add loading state
        target.classList.add('Button--loading');
        target.disabled = true;

        QuoteService.checkoutQuote(currentQuoteId, customerEmail)
        .then(data => {
          if (data.draftOrder && data.draftOrder.invoiceUrl) {
            window.location.href = data.draftOrder.invoiceUrl;
          } else {
            throw new Error('Invalid response: missing invoice URL');
          }
        })
        .catch(error => {
          console.error('Error processing checkout:', error);

          // 4xx responses carry a customer-readable reason (e.g. quote still syncing)
          const isClientError = error.status >= 400 && error.status < 500;
          showCheckoutError(
            isClientError
              ? error.message
              : 'Checkout could not be completed. Please try again, or contact your sales representative for assistance.'
          );

          // Remove loading state on error
          target.classList.remove('Button--loading');
          target.disabled = false;
        });

        return;
      }

      // Handle extend quote button - opens confirmation modal
      if (target && target.id === 'extend-quote-btn') {
        e.preventDefault();

        const currentQuoteId = getQuoteIdFromURL();
        if (!currentQuoteId) {
          console.error('No quote ID found.');
          return;
        }

        if (!window.TradeExtendQuoteModal) {
          console.error('Extend quote modal not loaded.');
          return;
        }

        window.TradeExtendQuoteModal.open(currentQuoteId);
        return;
      }

      if (window.handleTradeExportPdfClick({
        event: e,
        buttonId: 'export-quote-btn',
        getRecordId: getQuoteIdFromURL,
        endpointBuilder: (id) => `${window.tradeApiBase}/quotes/${id}/export-pdf`,
        label: 'Quote',
        dateLabel: 'Expiry date'
      })) {
        return;
      }
    });

    searchSortController.bind();
    pageController.initialize(fetchQuote, fetchQuotes);

    window.QuoteService.subscribe('quote:loading', function (loading) {
      if (!loading) {
        finishPendingActiveQuoteCartEdit();
      }
    });

    // Re-render detail view when the active quote is updated via cart
    window.QuoteService.subscribe('quote:changed', function (quoteData) {
      if (!quoteData) return;
      const viewingId = getQuoteIdFromURL();
      if (viewingId && String(quoteData.id) === String(viewingId)) {
        finishPendingActiveQuoteCartEdit();
        renderSingleQuote(quoteData);
      }
    });

    // Re-render the detail view in place when an expired quote is extended
    document.addEventListener('trade:quote-extended', (e) => {
      const viewingId = getQuoteIdFromURL();
      if (!viewingId) return;

      const quote = e && e.detail && e.detail.quote;
      if (quote && String(quote.id) === String(viewingId)) {
        renderSingleQuote(quote);
        if (window.TradeEditQuoteModal && typeof window.TradeEditQuoteModal.render === 'function') {
          window.TradeEditQuoteModal.render(quote);
        }
      } else {
        // Event carried no usable quote — refetch to rebuild the view
        fetchQuote(viewingId);
      }
    });

    // Listen for newly created quotes to refresh list without full reload
    document.addEventListener('trade:quote-created', (e) => {
      const newId = e && e.detail && e.detail.id;
      // If viewing a specific quote, navigate to it; otherwise refresh list
      const hasId = !!getQuoteIdFromURL();
      if (newId && hasId) {
        window.location.href = `/pages/quotes?id=${newId}`;
      } else if (typeof fetchQuotes === 'function') {
        fetchQuotes();
      }
    });
  });
})();
